// Main Map Container with MapLibre GL - Google Maps-like Features

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Map as MapLibreMap, GeoJSONSource, NavigationControl, GeolocateControl, FullscreenControl, ScaleControl, AttributionControl, config } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useMapStore, useSearchStore } from '../../stores';
import { useNavStore } from '../../stores/navStore';
import { Marker, Popup } from 'maplibre-gl';
import { setWorkerUrl } from 'maplibre-gl';
// Vite copies this into dist/assets and hands back its hashed URL, so the
// worker is served from our own origin instead of a CDN.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?url';

// MapLibre v6 has no `workerUrl` MapOptions field — passing one is silently
// ignored, and the library falls back to resolving `maplibre-gl-worker.mjs`
// relative to the page, which 404s (the SPA fallback answers with text/html) and
// leaves a blank map with "Worker failed to load". `setWorkerUrl()` is the
// supported entry point and must run before any Map is constructed.
setWorkerUrl(maplibreWorkerUrl);
import type { Business, Coordinates } from '../../types';
import { calculateDistance, formatDistance } from '../../lib/utils';
import { cn } from '../../lib/utils';
import { formatDuration, formatRouteDistance } from '../../lib/routing';
import { formatAccuracy } from '../../lib/geolocation';
import { registerFitBounds } from '../../lib/mapBridge';
import { pickBestPlace } from '../../lib/placeInfo';
import PlaceInfoCard, { type PlaceInfo } from './PlaceInfoCard';

/**
 * Free map styles — 100% no API keys required.
 *
 * Vector styles (CARTO basemaps) support 3D buildings, labels and rich POI data.
 * Raster styles are defined inline from public tile servers (OSM, Esri, OpenTopoMap)
 * and support satellite imagery, but cannot render 3D buildings.
 */
/**
 * Vector styles do not agree on what their main source is called: CARTO calls
 * it `carto`, OpenFreeMap calls it `openmaptiles`, Mapbox-derived styles call it
 * `composite`. Rather than hard-coding one of them, find whichever source in the
 * *active* style actually carries a given source-layer.
 */
function findSourceForLayer(map: MapLibreMap, sourceLayer: string): string | null {
  const layers = (map.getStyle()?.layers ?? []) as any[];
  const hit = layers.find((l) => l['source-layer'] === sourceLayer && typeof l.source === 'string');
  return hit ? hit.source : null;
}

/** True when the active style has no vector tiles to inspect or extrude. */
function vectorSourceId(map: MapLibreMap): string | null {
  const layers = (map.getStyle()?.layers ?? []) as any[];
  const hit = layers.find((l) => l['source-layer'] && typeof l.source === 'string');
  return hit ? hit.source : null;
}

export const MAP_STYLES = {
  // ---- Vector styles: support 3D buildings ----
  streets: {
    name: 'Streets',
    url: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
    type: 'vector',
    icon: '🗺️',
    description: 'Street map with labels',
  },
  light: {
    name: 'Light',
    url: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
    type: 'vector',
    icon: '☀️',
    description: 'Clean minimal theme',
  },
  dark: {
    name: 'Dark',
    url: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
    type: 'vector',
    icon: '🌙',
    description: 'Dark theme for night',
  },
  // ---- Raster styles: satellite, topo, etc. ----
  satellite: {
    name: 'Satellite',
    type: 'raster',
    icon: '🛰️',
    description: 'Satellite imagery (Esri)',
    style: {
      version: 8,
      sources: {
        satellite: {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
          attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics',
        },
      },
      layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
    },
  },
  hybrid: {
    name: 'Hybrid',
    type: 'raster',
    icon: '🗺️🛰️',
    description: 'Satellite with street labels',
    style: {
      version: 8,
      sources: {
        satellite: {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
          attribution: 'Imagery &copy; Esri',
        },
        labels: {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
        },
      },
      layers: [
        { id: 'satellite', type: 'raster', source: 'satellite' },
        { id: 'labels', type: 'raster', source: 'labels' },
      ],
    },
  },
  osm: {
    name: 'OpenStreetMap',
    // Was `https://tile.openstreetmap.org/{z}/{x}/{y}.png`. That "standard"
    // raster layer stopped receiving regular tile updates years ago, so this
    // style was frozen in time. OpenFreeMap is keyless, built from the full
    // OSM planet with daily updates, and is vector — which also makes
    // click-to-inspect and 3D buildings work here for the first time.
    url: 'https://tiles.openfreemap.org/styles/liberty',
    type: 'vector',
    icon: '🌍',
    description: 'OpenStreetMap, updated daily',
  },
  outdoors: {
    name: 'Topo',
    type: 'raster',
    icon: '🏞️',
    description: 'Topographic with trails',
    style: {
      version: 8,
      sources: {
        topo: {
          type: 'raster',
          tiles: [
            'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
            'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
            'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          attribution: '&copy; OpenTopoMap (CC-BY-SA), &copy; OpenStreetMap contributors',
          maxzoom: 17,
        },
      },
      layers: [{ id: 'topo', type: 'raster', source: 'topo' }],
    },
  },
  terrain: {
    name: 'Terrain',
    type: 'raster',
    icon: '⛰️',
    description: 'Elevation shaded relief',
    style: {
      version: 8,
      sources: {
        terrain: {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
          attribution: 'Relief &copy; Esri',
          maxzoom: 13,
        },
        labels: {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
          ],
          tileSize: 256,
        },
      },
    },
  },
} as const;

/**
 * Resolve a style key to something MapLibre accepts:
 * a style URL string for vector styles, or an inline StyleSpecification object
 * for raster styles.
 */
function getStyleSpec(key: string): string | any {
  const style = MAP_STYLES[key as keyof typeof MAP_STYLES] ?? MAP_STYLES.streets;
  return 'style' in style ? style.style : style.url;
}

const LEAD_LAYER_ID = 'leads-layer';
const LEAD_SOURCE_ID = 'leads-source';
const HEATMAP_LAYER_ID = 'leads-heatmap';
const HEATMAP_SOURCE_ID = 'heatmap-source';
const CLUSTER_LAYER_ID = 'leads-clusters';
const CLUSTER_SOURCE_ID = 'cluster-source';
const SELECTED_LAYER_ID = 'selected-lead';
const SELECTED_SOURCE_ID = 'selected-source';

// Navigation layers
const ROUTE_CASING_LAYER_ID = 'route-casing';
const ROUTE_LAYER_ID = 'route-line';
const ROUTE_SOURCE_ID = 'route-source';
const ROUTE_ALT_LAYER_ID = 'route-alt';
const ROUTE_ALT_SOURCE_ID = 'route-alt-source';
const ACCURACY_LAYER_ID = 'device-accuracy';
const ACCURACY_SOURCE_ID = 'device-accuracy-source';

// Breadcrumb trail drawn while live tracking is on
const TRAIL_LAYER_ID = 'track-trail';
const TRAIL_SOURCE_ID = 'track-trail-source';

// Invisible hit-target layers so POIs / streets / parks that the style does
// not visually render can still be picked with queryRenderedFeatures().
const INSPECT_LAYER_IDS: Record<string, string> = {
  poi: 'inspect-poi',
  place: 'inspect-place',
  transportation_name: 'inspect-road',
  water_name: 'inspect-water',
  park: 'inspect-park',
  mountain_peak: 'inspect-peak',
  housenumber: 'inspect-house',
  aerodrome_label: 'inspect-aerodrome',
  building: 'inspect-building',
};

interface MapContainerProps {
  onLeadClick?: (lead: Business) => void;
  onLeadHover?: (lead: Business | null) => void;
  onMapMove?: (center: Coordinates, zoom: number) => void;
  onStyleChange?: (style: string) => void;
}

export function MapContainer({
  onLeadClick,
  onLeadHover,
  onMapMove,
  onStyleChange,
}: MapContainerProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const navControlRef = useRef<NavigationControl | null>(null);
  const geolocateRef = useRef<GeolocateControl | null>(null);
  const originMarkerRef = useRef<Marker | null>(null);
  const destMarkerRef = useRef<Marker | null>(null);
  const deviceMarkerRef = useRef<Marker | null>(null);
  const [placeInfo, setPlaceInfo] = useState<PlaceInfo | null>(null);

  const {
    route,
    activeRouteIndex,
    origin,
    destination,
    whereAmI,
    panel,
    trail,
  } = useNavStore();
  
  const {
    mapState,
    setMapState,
    leads,
    selectedLead,
    hoveredLeadId,
    filters,
    viewMode,
    isLoadingLeads,
  } = useMapStore();
  
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [show3D, setShow3D] = useState(false);
  const [compassVisible, setCompassVisible] = useState(true);

  // Compute current style info
  const currentStyle = useMemo(() => MAP_STYLES[mapState.style as keyof typeof MAP_STYLES] ?? MAP_STYLES.streets, [mapState.style]);
  const supports3D = currentStyle.type === 'vector';

  // Initialize MapLibre map with Google Maps-like defaults
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    try {
      const styleSpec = getStyleSpec(mapState.style);
      
      const map = new MapLibreMap({
        container: mapContainerRef.current,
        style: styleSpec,
        center: [mapState.center.lng, mapState.center.lat],
        zoom: mapState.zoom,
        bearing: mapState.bearing,
        pitch: mapState.pitch,
        renderWorldCopies: true,
        // CJK fallback fontstacks on CARTO/OpenFreeMap 404 (verified:
        // `HanWangHeiLight Regular`, `NanumBarunGothic Regular`). MapLibre then
        // logs a failed request for every affected glyph run. Rendering CJK
        // locally avoids the noise and produces better results anyway.
        localIdeographFontFamily: 'sans-serif',
        attributionControl: false, // We add our own below
      });

      mapRef.current = map;

      // Expose the map instance for debugging / e2e checks in dev only
      if (import.meta.env.DEV) {
        (window as any).__leadmap = map;
      }

      // Let panels frame bounds through the bridge
      const unregisterFit = registerFitBounds((bounds, opts) => {
        map.fitBounds(bounds, {
          padding: opts?.padding ?? 80,
          maxZoom: opts?.maxZoom ?? 16,
          duration: 900,
        });
      });

      // Add Google Maps-like controls
      setupControls(map);

      map.on('load', () => {
        setTimeout(() => map.resize(), 0);
        setIsMapLoaded(true);
        setupMapLayers(map);
        updateLeadLayer(map);
        
        if (mapState.show3DBuildings && supports3D) {
          enable3DBuildings(map);
        }
        
        // Enable terrain if terrain style
        if (mapState.style === 'terrain') {
          enableTerrain(map);
        }
      });

      map.on('moveend', () => {
        const center = map.getCenter();
        const zoom = map.getZoom();
        const bearing = map.getBearing();
        const pitch = map.getPitch();
        
        setMapState({ 
          center: { lat: center.lat, lng: center.lng },
          zoom,
          bearing,
          pitch,
        });
        onMapMove?.({ lat: center.lat, lng: center.lng }, zoom);
      });

      map.on('style.load', () => {
        setupMapLayers(map);
        updateLeadLayer(map);
        if (mapState.show3DBuildings && supports3D) {
          enable3DBuildings(map);
        }
      });

      map.on('error', (e: any) => {
        console.error('Map error:', e.error);
        setMapError(e.error?.message || 'Map error occurred');
      });

      // Mouse events for lead interaction
      map.on('mousemove', LEAD_LAYER_ID, (e: any) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const leadId = feature.properties?.id;
          if (leadId) {
            const lead = leads.find(l => l.id === leadId);
            if (lead) {
              map.getCanvas().style.cursor = 'pointer';
              onLeadHover?.(lead);
            }
          }
        }
      });

      map.on('mouseleave', LEAD_LAYER_ID, () => {
        map.getCanvas().style.cursor = '';
        onLeadHover?.(null);
      });

      map.on('click', LEAD_LAYER_ID, (e: any) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const leadId = feature.properties?.id;
          if (leadId) {
            const lead = leads.find(l => l.id === leadId);
            if (lead) {
              onLeadClick?.(lead);
            }
          }
        }
      });

      // Cluster click - zoom to cluster
      map.on('click', CLUSTER_LAYER_ID, async (e: any) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const clusterId = feature.properties?.cluster_id;
          const clusterSource = map.getSource(CLUSTER_SOURCE_ID) as GeoJSONSource;
          if (clusterSource && clusterId !== undefined) {
            try {
              const zoom = await clusterSource.getClusterExpansionZoom(clusterId);
              if (zoom) {
                map.easeTo({
                  center: e.lngLat,
                  zoom: zoom + 1,
                  duration: 500,
                });
              }
            } catch (err) {
              console.error('Cluster expansion error:', err);
            }
          }
        }
      });

      // ---- Click anywhere: describe the place under the cursor ----
      map.on('click', (e: any) => {
        // Ignore clicks that already hit a lead marker or cluster
        const hitLayerIds: string[] = [
          ...(e.features ?? []).map((f: any) => f.layer?.id).filter(Boolean),
        ];
        const leadLayerIds = [LEAD_LAYER_ID, `${CLUSTER_LAYER_ID}-circle`, ROUTE_LAYER_ID];
        if (hitLayerIds.some((id) => leadLayerIds.includes(id))) return;

        // Raster styles (satellite / OSM / topo) carry no inspectable tiles
        const styleInfo = MAP_STYLES[mapState.style as keyof typeof MAP_STYLES];
        if (!styleInfo || styleInfo.type !== 'vector') {
          setPlaceInfo(null);
          return;
        }

        const zoom = map.getZoom();
        // Query our invisible inspect layers (not raw source layers)
        const queryLayers = Object.values(INSPECT_LAYER_IDS).filter((id) =>
          map.getLayer(id)
        );

        let features: any[] = [];
        if (queryLayers.length) {
          try {
            features = map.queryRenderedFeatures(e.point, { layers: queryLayers }) as any[];
          } catch {
            features = [];
          }
        }

        // Map each hit feature back to its tile source-layer
        const decoded = features.map((f: any) => ({
          ...f,
          sourceLayerHint: f.layer?.id?.replace(/^inspect-/, '') ?? undefined,
        }));

        const info = pickBestPlace(decoded, { lng: e.lngLat.lng, lat: e.lngLat.lat });
        setPlaceInfo(info);
      });

      // Dismiss the info card when the map is dragged
      map.on('movestart', () => setPlaceInfo(null));

      // Keyboard shortcuts (Google Maps-like)
      const handleKeyDown = (event: KeyboardEvent) => {
        if (!mapRef.current) return;
        const map = mapRef.current;
        
        // Don't handle if user is typing in an input
        if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

        switch (event.key) {
          case '+':
          case '=':
            event.preventDefault();
            map.zoomIn({ duration: 200 });
            break;
          case '-':
            event.preventDefault();
            map.zoomOut({ duration: 200 });
            break;
          case 'ArrowUp':
            event.preventDefault();
            map.panBy([0, -100], { duration: 200 });
            break;
          case 'ArrowDown':
            event.preventDefault();
            map.panBy([0, 100], { duration: 200 });
            break;
          case 'ArrowLeft':
            event.preventDefault();
            map.panBy([-100, 0], { duration: 200 });
            break;
          case 'ArrowRight':
            event.preventDefault();
            map.panBy([100, 0], { duration: 200 });
            break;
          case 'n':
          case 'N':
            event.preventDefault();
            map.easeTo({ bearing: 0, duration: 500 });
            break;
          case 'r':
          case 'R':
            event.preventDefault();
            map.easeTo({ pitch: map.getPitch() === 0 ? 60 : 0, duration: 500 });
            break;
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        unregisterFit();
        mapRef.current?.remove();
        mapRef.current = null;
      };
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError('Failed to initialize map');
    }
  }, []);

  const setupControls = (map: MapLibreMap) => {
    // NOTE: The top-right corner is reserved for our custom <MapControls />
    // panel, so all native MapLibre controls are docked bottom-right /
    // bottom-left to avoid overlapping it.

    // Zoom + compass + tilt — bottom-right
    const nav = new NavigationControl({
      showCompass: true,
      showZoom: true,
      visualizePitch: true,
    });
    map.addControl(nav, 'bottom-right');
    navControlRef.current = nav;

    // My-location — bottom-right
    const geolocate = new GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserLocation: true,
      fitBoundsOptions: { maxZoom: 16 },
    });
    map.addControl(geolocate, 'bottom-right');
    geolocateRef.current = geolocate;

    // Fullscreen — bottom-right
    map.addControl(new FullscreenControl(), 'bottom-right');

    // Metric scale bar — bottom-left
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');

    // Attribution (required by OSM / Esri / CARTO) — bottom-left
    map.addControl(new AttributionControl({ compact: true }), 'bottom-left');
  };

  const setupMapLayers = (map: MapLibreMap) => {
    // Lead source with clustering
    if (!map.getSource(LEAD_SOURCE_ID)) {
      map.addSource(LEAD_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    }

    // Cluster source
    if (!map.getSource(CLUSTER_SOURCE_ID)) {
      map.addSource(CLUSTER_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    }

    // Heatmap source
    if (!map.getSource(HEATMAP_SOURCE_ID)) {
      map.addSource(HEATMAP_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Selected lead source
    if (!map.getSource(SELECTED_SOURCE_ID)) {
      map.addSource(SELECTED_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Lead layers (unclustered points)
    if (!map.getLayer(LEAD_LAYER_ID)) {
      map.addLayer({
        id: LEAD_LAYER_ID,
        type: 'circle',
        source: LEAD_SOURCE_ID,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            10, 6, 15, 10, 20, 14,
          ],
          'circle-color': [
            'match', ['get', 'leadType'],
            'no-website', '#ef4444',
            'marketing', '#f59e0b',
            'b2b', '#3b82f6',
            'multiple', '#a855f7',
            '#64748b',
          ],
          'circle-opacity': 0.9,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
    }

    // Cluster circles
    if (!map.getLayer(`${CLUSTER_LAYER_ID}-circle`)) {
      map.addLayer({
        id: `${CLUSTER_LAYER_ID}-circle`,
        type: 'circle',
        source: CLUSTER_SOURCE_ID,
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': [
            'step', ['get', 'point_count'],
            20, 10, 25, 50, 30, 100, 35, 500, 40,
          ],
          'circle-color': [
            'step', ['get', 'point_count'],
            '#3b82f6', 10, '#0ea5e9', 50, '#06b6d4', 100, '#22d3ee', 500, '#67e8f9',
          ],
          'circle-opacity': 0.85,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
    }

    // Cluster count labels
    if (!map.getLayer(`${CLUSTER_LAYER_ID}-label`)) {
      map.addLayer({
        id: `${CLUSTER_LAYER_ID}-label`,
        type: 'symbol',
        source: CLUSTER_SOURCE_ID,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 1,
        },
      });
    }

    // Heatmap layer
    if (!map.getLayer(HEATMAP_LAYER_ID)) {
      map.addLayer({
        id: HEATMAP_LAYER_ID,
        type: 'heatmap',
        source: HEATMAP_SOURCE_ID,
        maxzoom: 15,
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'leadScore'], 0, 0, 100, 1],
          'heatmap-intensity': 1,
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.2, 'rgb(103,169,207)',
            0.4, 'rgb(209,229,240)',
            0.6, 'rgb(253,219,199)',
            0.8, 'rgb(244,165,130)',
            1, 'rgb(204,69,43)',
          ],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 15, 15, 30],
          'heatmap-opacity': showHeatmap ? 0.8 : 0,
        },
      });
    }

    // Selected lead highlight
    if (!map.getLayer(SELECTED_LAYER_ID)) {
      map.addLayer({
        id: SELECTED_LAYER_ID,
        type: 'circle',
        source: SELECTED_SOURCE_ID,
        paint: {
          'circle-radius': 18,
          'circle-color': '#0ea5e9',
          'circle-opacity': 0.3,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#0ea5e9',
        },
      });
    }

    /* ---------------- Navigation layers ---------------- */

    // Device accuracy circle
    if (!map.getSource(ACCURACY_SOURCE_ID)) {
      map.addSource(ACCURACY_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(ACCURACY_LAYER_ID)) {
      map.addLayer({
        id: ACCURACY_LAYER_ID,
        type: 'circle',
        source: ACCURACY_SOURCE_ID,
        paint: {
          // Small by default; the accuracy effect resizes it to the real
          // GPS radius but keeps it bounded so it never swamps the dot.
          'circle-radius': 10,
          'circle-color': '#38bdf8',
          'circle-opacity': 0.1,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#0ea5e9',
          'circle-stroke-opacity': 0.35,
        },
      });
    }

    // Breadcrumb trail (live tracking)
    if (!map.getSource(TRAIL_SOURCE_ID)) {
      map.addSource(TRAIL_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(TRAIL_LAYER_ID)) {
      map.addLayer({
        id: TRAIL_LAYER_ID,
        type: 'line',
        source: TRAIL_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#0ea5e9',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 16, 5],
          'line-opacity': 0.85,
        },
      });
    }

    // Alternate routes (drawn under the active one)
    if (!map.getSource(ROUTE_ALT_SOURCE_ID)) {
      map.addSource(ROUTE_ALT_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(ROUTE_ALT_LAYER_ID)) {
      map.addLayer({
        id: ROUTE_ALT_LAYER_ID,
        type: 'line',
        source: ROUTE_ALT_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#94a3b8',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 16, 4.5],
          'line-opacity': 0.55,
          'line-dasharray': [2, 2],
        },
      });
    }

    // Active route: casing underneath + coloured line on top
    if (!map.getSource(ROUTE_SOURCE_ID)) {
      map.addSource(ROUTE_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer(ROUTE_CASING_LAYER_ID)) {
      map.addLayer({
        id: ROUTE_CASING_LAYER_ID,
        type: 'line',
        source: ROUTE_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 6, 16, 11],
          'line-opacity': 0.9,
        },
      });
    }
    if (!map.getLayer(ROUTE_LAYER_ID)) {
      map.addLayer({
        id: ROUTE_LAYER_ID,
        type: 'line',
        source: ROUTE_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#0284c7',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 3.5, 16, 7],
          'line-opacity': 0.95,
        },
      });
    }

    setupInspectLayers(map);
  };

  /**
   * Add fully transparent layers over the vector source-layers we want to be
   * able to inspect. They render nothing but are returned by
   * queryRenderedFeatures(), which is what powers click-to-inspect.
   */
  const setupInspectLayers = (map: MapLibreMap) => {
    // Only vector styles have a queryable tile source
    const styleInfo = MAP_STYLES[mapState.style as keyof typeof MAP_STYLES];
    if (!styleInfo || styleInfo.type !== 'vector') return;

    const styleLayers = (map.getStyle()?.layers ?? []) as any[];
    const available = new Set(
      styleLayers.map((l: any) => l['source-layer']).filter(Boolean)
    );

    // Raster styles have no source-layers at all — nothing to inspect.
    if (available.size === 0) return;

    for (const [sourceLayer, layerId] of Object.entries(INSPECT_LAYER_IDS)) {
      if (!available.has(sourceLayer)) continue;
      if (map.getLayer(layerId)) continue;

      // Resolve the source from the active style instead of hard-coding
      // `carto`, so inspection also works on the OpenFreeMap-based OSM style.
      const source = findSourceForLayer(map, sourceLayer);
      if (!source) continue;

      const isPoint = sourceLayer === 'poi' || sourceLayer === 'place' ||
                      sourceLayer === 'mountain_peak' || sourceLayer === 'aerodrome_label' ||
                      sourceLayer === 'housenumber' || sourceLayer === 'water_name' ||
                      sourceLayer === 'park';

      try {
        if (isPoint) {
          // Generous invisible circle so small POIs are easy to click
          map.addLayer({
            id: layerId,
            type: 'circle',
            source,
            'source-layer': sourceLayer,
            minzoom: 0,
            paint: {
              'circle-radius': 14,
              'circle-color': '#000000',
              'circle-opacity': 0.0001,
            },
          });
        } else if (sourceLayer === 'transportation_name') {
          map.addLayer({
            id: layerId,
            type: 'line',
            source,
            'source-layer': sourceLayer,
            minzoom: 0,
            paint: {
              'line-color': '#000000',
              'line-width': 16,
              'line-opacity': 0.0001,
            },
          });
        } else {
          map.addLayer({
            id: layerId,
            type: 'fill',
            source,
            'source-layer': sourceLayer,
            minzoom: 0,
            paint: {
              'fill-color': '#000000',
              'fill-opacity': 0.0001,
            },
          });
        }
      } catch (err) {
        // A missing source-layer for this style is not fatal
        console.debug('[inspect] could not add layer for', sourceLayer, err);
      }
    }
  };

  /* ------------------------------------------------------------- */
  /*  Navigation (route + device position) rendering                */
  /* ------------------------------------------------------------- */

  const syncNavLayers = (map: MapLibreMap) => {
    // --- route line ---
    const routeSource = map.getSource(ROUTE_SOURCE_ID) as GeoJSONSource | undefined;
    const altSource = map.getSource(ROUTE_ALT_SOURCE_ID) as GeoJSONSource | undefined;

    const all = route?.routes ?? [];
    const active = all.length
      ? all[Math.min(activeRouteIndex, all.length - 1)]
      : null;
    const alts = all.filter((_, i) => i !== activeRouteIndex);

    const toFC = (r: typeof all[number] | null) => ({
      type: 'FeatureCollection' as const,
      features: r
        ? [
            {
              type: 'Feature' as const,
              geometry: { type: 'LineString' as const, coordinates: r.coordinates },
              properties: {},
            },
          ]
        : [],
    });

    routeSource?.setData(toFC(active));
    altSource?.setData({
      type: 'FeatureCollection',
      features: alts.map((r) => ({
        type: 'Feature' as const,
        geometry: { type: 'LineString' as const, coordinates: r.coordinates },
        properties: {},
      })),
    });

    // --- accuracy circle (circle geometry sized by accuracy in metres) ---
    const accSource = map.getSource(ACCURACY_SOURCE_ID) as GeoJSONSource | undefined;
    if (accSource) {
      if (whereAmI) {
        accSource.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [whereAmI.coords.lng, whereAmI.coords.lat],
              },
              properties: {
                // radius expressed in pixels is approximated by zoom above
                accuracy: whereAmI.accuracy,
              },
            },
          ],
        });
      } else {
        accSource.setData({ type: 'FeatureCollection', features: [] });
      }
    }

    // --- markers ---
    syncMarker(map, originMarkerRef, 'origin', origin.coords, '#0284c7', origin.label);
    syncMarker(map, destMarkerRef, 'destination', destination.coords, '#dc2626', destination.label);
    syncMarker(map, deviceMarkerRef, 'device', whereAmI?.coords ?? null, '#0ea5e9', whereAmI?.address ?? 'My location');

    // --- breadcrumb trail ---
    const trailSource = map.getSource(TRAIL_SOURCE_ID) as GeoJSONSource | undefined;
    if (trailSource) {
      trailSource.setData({
        type: 'FeatureCollection',
        features: trail.length >= 2
          ? [
              {
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: trail },
                properties: {},
              },
            ]
          : [],
      });
    }

    // Rotate the device dot to the reported heading
    if (whereAmI?.heading != null && deviceMarkerRef.current) {
      const core = deviceMarkerRef.current
        .getElement()
        .querySelector('.leadmap3d-device-core') as HTMLElement | null;
      if (core) core.style.rotate = `${whereAmI.heading}deg`;
    }
  };

  const syncMarker = (
    map: MapLibreMap,
    ref: React.MutableRefObject<Marker | null>,
    kind: 'origin' | 'destination' | 'device',
    coords: Coordinates | null,
    color: string,
    label: string
  ) => {
    if (!coords) {
      ref.current?.remove();
      ref.current = null;
      return;
    }

    if (!ref.current) {
      // Build the marker once; later updates only move it.
      const el = document.createElement('div');
      el.className = `leadmap3d-marker leadmap3d-marker--${kind}`;

      if (kind === 'device') {
        // Google-Maps style location dot: pulsing halo + solid core.
        el.innerHTML = `
          <span class="leadmap3d-device-halo"></span>
          <span class="leadmap3d-device-core"></span>
        `;
        el.style.width = '18px';
        el.style.height = '18px';
      } else {
        const dot = document.createElement('span');
        dot.className = 'leadmap3d-endpoint';
        dot.style.background = color;
        el.appendChild(dot);
        el.style.width = '18px';
        el.style.height = '18px';
      }

      const popup = new Popup({ offset: 14, closeButton: false, maxWidth: '260px' })
        .setDOMContent(
          Object.assign(document.createElement('div'), {
            className: 'px-1 py-0.5 text-xs',
            textContent: label || '',
          })
        );

      ref.current = new Marker({ element: el, anchor: 'center' })
        .setLngLat([coords.lng, coords.lat])
        .setPopup(popup)
        .addTo(map);
    } else {
      // Just reposition - never replace the element, or the Marker loses it.
      ref.current.setLngLat([coords.lng, coords.lat]);
      if (label) {
        const content = ref.current.getPopup()?.getElement()?.firstElementChild;
        if (content) content.textContent = label;
      }
    }
  };

  const updateLeadLayer = (map: MapLibreMap) => {
    const filteredLeads = leads.filter(lead => {
      if (filters.categories.length > 0 && !filters.categories.includes(lead.category)) return false;
      if (filters.leadTypes.length > 0 && !filters.leadTypes.includes(lead.leadType)) return false;
      if (lead.leadScore < filters.minLeadScore || lead.leadScore > filters.maxLeadScore) return false;
      if (filters.hasWebsite !== null && lead.hasWebsite !== filters.hasWebsite) return false;
      if (lead.rating !== undefined && (lead.rating < filters.minRating || lead.rating > filters.maxRating)) return false;
      if (lead.reviewCount !== undefined && (lead.reviewCount < filters.minReviews || lead.reviewCount > filters.maxReviews)) return false;
      if (filters.keywords.length > 0) {
        const searchText = `${lead.name} ${lead.category} ${lead.address} ${lead.description || ''}`.toLowerCase();
        if (!filters.keywords.some(kw => searchText.includes(kw.toLowerCase()))) return false;
      }
      const distance = calculateDistance(filters.center, lead.coordinates);
      if (distance > filters.radius) return false;
      return true;
    });

    const features = filteredLeads.map(lead => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [lead.coordinates.lng, lead.coordinates.lat] },
      properties: {
        id: lead.id,
        name: lead.name,
        category: lead.category,
        leadType: lead.leadType,
        leadScore: lead.leadScore,
        hasWebsite: lead.hasWebsite,
        rating: lead.rating || 0,
        reviewCount: lead.reviewCount || 0,
      },
    }));

    const sources = [
      LEAD_SOURCE_ID, CLUSTER_SOURCE_ID, HEATMAP_SOURCE_ID
    ].map(id => map.getSource(id) as GeoJSONSource | undefined);

    sources.forEach(source => {
      if (source) source.setData({ type: 'FeatureCollection', features });
    });
  };

  const enable3DBuildings = (map: MapLibreMap) => {
    if (map.getLayer('3d-buildings')) return;

    // Some styles (OpenFreeMap) already ship their own extrusion layer, so
    // adding ours on top would double-render every building.
    if (map.getLayer('building-3d')) {
      // Make sure the style's own layer is actually visible.
      const own = map.getLayer('building-3d') as any;
      if (own.layout && own.layout.visibility === 'none') {
        map.setLayoutProperty('building-3d', 'visibility', 'visible');
      }
      return;
    }

    // Previously this looked for a source literally named `composite` (a
    // Mapbox convention). CARTO names its source `carto` and OpenFreeMap
    // `openmaptiles`, so the layer was never added and 3D never rendered.
    const source = findSourceForLayer(map, 'building');
    if (!source) return;

    try {
      map.addLayer({
        id: '3d-buildings',
        source,
        'source-layer': 'building',
        type: 'fill-extrusion',
        minzoom: 14,
        filter: ['!=', ['get', 'hide_3d'], true],
        paint: {
          'fill-extrusion-color': '#aaa',
          // The OpenMapTiles schema these styles use names these
          // `render_height` / `render_min_height`, not `height` / `min_height`.
          // Reading the wrong names yielded null and flat, invisible buildings.
          'fill-extrusion-height': [
            'coalesce',
            ['get', 'render_height'],
            ['get', 'height'],
            8,
          ],
          'fill-extrusion-base': [
            'coalesce',
            ['get', 'render_min_height'],
            ['get', 'min_height'],
            0,
          ],
          'fill-extrusion-opacity': 0.6,
        },
      });
    } catch (e) {
      console.log('3D buildings not available for this style', e);
    }
  };

  const disable3DBuildings = (map: MapLibreMap) => {
    if (map.getLayer('3d-buildings')) map.removeLayer('3d-buildings');
  };

  const enableTerrain = (map: MapLibreMap) => {
    if (map.getTerrain()) return;
    try {
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14,
      });
      map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
    } catch (e) {
      console.log('Terrain not available:', e);
    }
  };

  const disableTerrain = (map: MapLibreMap) => {
    if (map.getTerrain()) map.setTerrain(null);
  };

  // Update selected lead highlight
  useEffect(() => {
    if (!mapRef.current || !isMapLoaded) return;
    const map = mapRef.current;
    const source = map.getSource(SELECTED_SOURCE_ID) as GeoJSONSource;
    
    if (selectedLead) {
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [selectedLead.coordinates.lng, selectedLead.coordinates.lat] },
            properties: { id: selectedLead.id },
          }],
        });
      }
    } else if (source) {
      source.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [selectedLead, isMapLoaded]);

  // Hide the native bottom-right control stack while a navigation panel is
// docked on the right, otherwise the panel covers it entirely.
useEffect(() => {
  if (!mapRef.current || !isMapLoaded) return;
  const map = mapRef.current;
  const stack = map.getContainer().querySelector('.maplibregl-ctrl-bottom-right') as HTMLElement | null;
  if (stack) stack.style.display = panel === 'none' ? '' : 'none';
}, [panel, isMapLoaded]);

// Sync navigation layers (route line, alternatives, accuracy, markers)
  useEffect(() => {
    if (!mapRef.current || !isMapLoaded) return;
    syncNavLayers(mapRef.current);
  }, [
    route,
    activeRouteIndex,
    origin.coords?.lat,
    origin.coords?.lng,
    destination.coords?.lat,
    destination.coords?.lng,
    whereAmI?.coords.lat,
    whereAmI?.coords.lng,
    whereAmI?.heading,
    trail,
    isMapLoaded,
  ]);

  // Keep the accuracy circle the right size on zoom
  useEffect(() => {
    if (!mapRef.current || !isMapLoaded) return;
    const map = mapRef.current;
    if (map.getLayer(ACCURACY_LAYER_ID) && whereAmI) {
      const acc = Math.max(5, whereAmI.accuracy || 20);
      // metres -> pixels at this latitude + zoom
      const metresPerPixel =
        (156543.03392 * Math.cos((map.getCenter().lat * Math.PI) / 180)) /
        Math.pow(2, map.getZoom());
      const px = acc / metresPerPixel;
      // Cap it: an inaccurate fix should not paint over half the viewport.
      map.setPaintProperty(
        ACCURACY_LAYER_ID,
        'circle-radius',
        Math.min(70, Math.max(6, px))
      );
    }
  }, [whereAmI?.coords.lat, whereAmI?.coords.lng, whereAmI?.accuracy, mapState.zoom, mapState.center.lat, isMapLoaded]);

  // Cleanup markers on unmount
  useEffect(() => {
    return () => {
      originMarkerRef.current?.remove();
      destMarkerRef.current?.remove();
      deviceMarkerRef.current?.remove();
    };
  }, []);

  // Update map style
  useEffect(() => {
    if (mapRef.current && isMapLoaded) {
      mapRef.current.setStyle(getStyleSpec(mapState.style));
      onStyleChange?.(mapState.style);
      
      // Update 3D/terrain support for new style
      const newStyle = MAP_STYLES[mapState.style as keyof typeof MAP_STYLES] || MAP_STYLES.streets;
      if (newStyle.type === 'vector') {
        if (mapState.show3DBuildings) enable3DBuildings(mapRef.current);
        if (mapState.style === 'terrain') enableTerrain(mapRef.current);
      } else {
        disable3DBuildings(mapRef.current);
        disableTerrain(mapRef.current);
      }
    }
  }, [mapState.style, isMapLoaded]);

  // Update 3D buildings
  useEffect(() => {
    if (mapRef.current && isMapLoaded && supports3D) {
      if (mapState.show3DBuildings) enable3DBuildings(mapRef.current);
      else disable3DBuildings(mapRef.current);
    }
  }, [mapState.show3DBuildings, isMapLoaded, supports3D]);

  // Update heatmap
  useEffect(() => {
    if (mapRef.current && isMapLoaded) {
      const map = mapRef.current;
      if (map.getLayer(HEATMAP_LAYER_ID)) {
        map.setPaintProperty(HEATMAP_LAYER_ID, 'heatmap-opacity', showHeatmap ? 0.8 : 0);
      }
    }
  }, [showHeatmap, isMapLoaded]);

  // Sync map state
  useEffect(() => {
    if (mapRef.current && isMapLoaded) {
      const map = mapRef.current;
      const needsMove = 
        Math.abs(map.getCenter().lat - mapState.center.lat) > 0.0001 ||
        Math.abs(map.getCenter().lng - mapState.center.lng) > 0.0001 ||
        Math.abs(map.getZoom() - mapState.zoom) > 0.01;
      
      if (needsMove) {
        map.jumpTo({
          center: [mapState.center.lng, mapState.center.lat],
          zoom: mapState.zoom,
          bearing: mapState.bearing,
          pitch: mapState.pitch,
        });
      }
    }
  }, [mapState.center, mapState.zoom, mapState.bearing, mapState.pitch, isMapLoaded]);

  // Public methods
  const flyTo = useCallback((coordinates: Coordinates, zoom?: number) => {
    mapRef.current?.flyTo({
      center: [coordinates.lng, coordinates.lat],
      zoom: zoom ?? 16,
      duration: 1000,
      essential: true,
    });
  }, []);

  const fitBounds = useCallback((bounds: [[number, number], [number, number]], padding = 50) => {
    mapRef.current?.fitBounds(bounds, { padding, duration: 1000 });
  }, []);

  const recenter = useCallback(() => {
    if (geolocateRef.current) geolocateRef.current.trigger();
  }, []);

  const toggleNorthUp = useCallback(() => {
    mapRef.current?.easeTo({ bearing: 0, duration: 500 });
  }, []);

  const togglePitch = useCallback(() => {
    const map = mapRef.current;
    if (map) map.easeTo({ pitch: map.getPitch() === 0 ? 60 : 0, duration: 500 });
  }, []);

  const zoomIn = useCallback(() => mapRef.current?.zoomIn({ duration: 200 }), []);
  const zoomOut = useCallback(() => mapRef.current?.zoomOut({ duration: 200 }), []);

  return (
    <div className="relative w-full h-full">
      {mapError && (
        <div className="absolute top-4 left-4 right-4 z-50 bg-red-500/90 text-white px-4 py-3 rounded-lg shadow-lg flex items-center justify-between animate-slide-down">
          <span>Map Error: {mapError}</span>
          <button onClick={() => window.location.reload()} className="text-white hover:text-slate-200 font-medium">
            Reload
          </button>
        </div>
      )}

      <div 
        ref={mapContainerRef} 
        className="w-full h-full min-h-[400px]"
        style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%' }}
      />

      {/* Place info card, anchored to the clicked point */}
      {placeInfo && mapRef.current && (
        <PlaceInfoPopup
          place={placeInfo}
          map={mapRef.current}
          onClose={() => setPlaceInfo(null)}
        />
      )}

      {/* Loading indicator */}
      {isLoadingLeads && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 animate-fade-in">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-medium text-slate-700">Loading leads...</span>
        </div>
      )}

      {/* Lead count & status badges */}
      {isMapLoaded && (
        <div className="absolute bottom-4 left-4 right-4 z-30 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-slate-200 flex items-center gap-2 animate-fade-in sm:justify-start">
            <div className="w-2 h-2 rounded-full bg-primary-500"></div>
            <span className="text-sm font-medium text-slate-700">
              {leads.length} lead{leads.length !== 1 ? 's' : ''} in view
            </span>
            {showHeatmap && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-red-500"></span>
                Heatmap
              </span>
            )}
            {show3D && supports3D && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                3D Buildings
              </span>
            )}
          </div>
          
          {/* Coordinates display */}
          <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg border border-slate-200 text-xs text-slate-600 font-mono animate-fade-in">
            {mapState.center.lat.toFixed(4)}°, {mapState.center.lng.toFixed(4)}°  •  Zoom: {mapState.zoom.toFixed(1)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Place info popup wrapper                                          */
/* ------------------------------------------------------------------ */

function PlaceInfoPopup({
  place,
  map,
  onClose,
}: {
  place: PlaceInfo;
  map: MapLibreMap;
  onClose: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<Popup | null>(null);
  const [anchor, setAnchor] = useState<{ lng: number; lat: number } | null>(null);

  // Create / update the MapLibre popup. The card is rendered by React into a
  // detached container that we hand to MapLibre, so no nested React root is
  // needed (which would crash the tree).
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    if (!popupRef.current) {
      popupRef.current = new Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -14],
        maxWidth: 'none',
        className: 'leadmap3d-place-popup',
      }).setDOMContent(el).addTo(map);
    }

    setAnchor({ lng: place.lng, lat: place.lat });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
    };
  }, [map]);

  // Keep the popup anchored to the clicked point
  useEffect(() => {
    if (anchor && popupRef.current) {
      popupRef.current.setLngLat([anchor.lng, anchor.lat]);
    }
  }, [anchor]);

  return (
    <div ref={contentRef} className="contents">
      <PlaceInfoCard place={place} onClose={onClose} />
    </div>
  );
}

export default MapContainer;